/**
 * Category Tabs Component
 * Horizontal scrollable category tabs with subcategory support
 */

import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';

interface Category {
  id: number | string;
  name: string;
  product_count?: number;
  subcategories?: Subcategory[];
}

interface Subcategory {
  id: number | string;
  name: string;
  product_count?: number;
}

interface CategoryTabsProps {
  categories: Category[];
  selectedCategory: number | string | null;
  selectedSubcategory?: number | string | null;
  onCategorySelect: (categoryId: number | string | null) => void;
  onSubcategorySelect?: (subcategoryId: number | string | null) => void;
  showAllTab?: boolean;
}

const CategoryTabs: React.FC<CategoryTabsProps> = ({
  categories,
  selectedCategory,
  selectedSubcategory,
  onCategorySelect,
  onSubcategorySelect,
  showAllTab = true
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const subcategoryScrollRef = useRef<HTMLDivElement>(null);

  // Get subcategories for selected category
  const currentCategory = categories.find(cat => cat.id === selectedCategory);
  const subcategories = currentCategory?.subcategories || [];

  // Scroll selected tab into view
  useEffect(() => {
    const selectedElement = scrollRef.current?.querySelector('[data-selected="true"]');
    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [selectedCategory]);

  // Scroll selected subcategory into view
  useEffect(() => {
    const selectedElement = subcategoryScrollRef.current?.querySelector('[data-selected="true"]');
    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [selectedSubcategory]);

  const handleTabClick = (categoryId: number | string | null) => {
    onCategorySelect(categoryId);
    // Reset subcategory when category changes
    if (onSubcategorySelect) {
      onSubcategorySelect(null);
    }
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
  };

  const handleSubcategoryClick = (subcategoryId: number | string | null) => {
    if (onSubcategorySelect) {
      onSubcategorySelect(subcategoryId);
    }
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
  };

  return (
    <TabsContainer>
      {/* Main Categories */}
      <TabsWrapper ref={scrollRef}>
        {showAllTab && (
          <Tab
            onClick={() => handleTabClick(null)}
            $selected={selectedCategory === null}
            data-selected={selectedCategory === null}
          >
            All
          </Tab>
        )}

        {categories.map((category) => (
          <Tab
            key={category.id}
            onClick={() => handleTabClick(category.id)}
            $selected={selectedCategory === category.id}
            data-selected={selectedCategory === category.id}
          >
            {category.name}
            {category.product_count !== undefined && category.product_count > 0 && (
              <TabBadge $selected={selectedCategory === category.id}>
                {category.product_count}
              </TabBadge>
            )}
          </Tab>
        ))}
      </TabsWrapper>

      {/* Subcategories (shown when a category with subcategories is selected) */}
      {subcategories.length > 0 && (
        <SubcategoryWrapper ref={subcategoryScrollRef}>
          <SubcategoryTab
            onClick={() => handleSubcategoryClick(null)}
            $selected={selectedSubcategory === null}
            data-selected={selectedSubcategory === null}
          >
            All {currentCategory?.name}
          </SubcategoryTab>

          {subcategories.map((sub) => (
            <SubcategoryTab
              key={sub.id}
              onClick={() => handleSubcategoryClick(sub.id)}
              $selected={selectedSubcategory === sub.id}
              data-selected={selectedSubcategory === sub.id}
            >
              {sub.name}
              {sub.product_count !== undefined && sub.product_count > 0 && (
                <SubcategoryBadge>{sub.product_count}</SubcategoryBadge>
              )}
            </SubcategoryTab>
          ))}
        </SubcategoryWrapper>
      )}
    </TabsContainer>
  );
};

const TabsContainer = styled.div`
  background: var(--tg-theme-bg-color, #1a1a1a);
`;

const TabsWrapper = styled.div`
  display: flex;
  overflow-x: auto;
  padding: 10px 16px;
  gap: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 16px;
  border: none;
  background: ${props => props.$selected
    ? 'rgba(var(--tg-theme-link-color-rgb, 0, 255, 136), 0.15)'
    : 'var(--tg-theme-secondary-bg-color, #2c2c2e)'};
  color: ${props => props.$selected
    ? 'var(--tg-theme-link-color, #00ff88)'
    : 'var(--tg-theme-text-color, #ffffff)'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.$selected
      ? 'rgba(var(--tg-theme-link-color-rgb, 0, 255, 136), 0.2)'
      : 'var(--tg-theme-button-color, #3390ec)'};
    color: ${props => props.$selected
      ? 'var(--tg-theme-link-color, #00ff88)'
      : 'var(--tg-theme-button-text-color, #ffffff)'};
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TabBadge = styled.span<{ $selected: boolean }>`
  background: ${props => props.$selected
    ? 'rgba(var(--tg-theme-link-color-rgb, 0, 255, 136), 0.3)'
    : 'rgba(255, 255, 255, 0.15)'};
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
`;

const SubcategoryWrapper = styled.div`
  display: flex;
  overflow-x: auto;
  padding: 8px 16px 12px;
  gap: 8px;
  border-top: 1px solid var(--tg-theme-secondary-bg-color, #2c2c2e);
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const SubcategoryTab = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 12px;
  border: 1px solid ${props => props.$selected
    ? 'var(--tg-theme-link-color, #00ff88)'
    : 'var(--tg-theme-hint-color, #48484a)'};
  background: ${props => props.$selected
    ? 'rgba(var(--tg-theme-link-color-rgb, 0, 255, 136), 0.1)'
    : 'transparent'};
  color: ${props => props.$selected
    ? 'var(--tg-theme-link-color, #00ff88)'
    : 'var(--tg-theme-hint-color, #8e8e93)'};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:active {
    transform: scale(0.95);
  }
`;

const SubcategoryBadge = styled.span`
  background: rgba(255, 255, 255, 0.1);
  padding: 1px 5px;
  border-radius: 8px;
  font-size: 9px;
`;

export default CategoryTabs;
